import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Reader } from '@/services/rfidService';
import toast from '@/utils/toast';

interface ReaderMqttConfigDialogProps {
  open: boolean;
  reader: Reader | null;
  onClose: () => void;
}

const ReaderMqttConfigDialog = ({ open, reader, onClose }: ReaderMqttConfigDialogProps) => {
  const config = reader?.mqtt_config;
  const entries = config ? [
    ['Hostname', config.hostname],
    ['Port', String(config.port)],
    ['Client ID', config.client_id],
    ['Username', config.username],
    ['Password', config.password],
    ['Publish topic', config.publish_topic],
    ['Subscription topic', config.subscription_topic],
    ['Keep alive', String(config.keep_alive)],
  ] : [];

  const copy = async (value: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch {
      toast.error('Unable to copy configuration');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Reader MQTT Configuration</DialogTitle>
      <DialogContent dividers>
        {config ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              These values are generated by AMS and cannot be edited here.
            </Typography>
            {entries.map(([label, value]) => (
              <Box key={label} sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography component="code" sx={{ display: 'block', overflowWrap: 'anywhere', userSelect: 'all' }}>
                      {value || '(blank)'}
                    </Typography>
                  </Box>
                  <Tooltip title={`Copy ${label}`}>
                    <IconButton aria-label={`Copy ${label}`} onClick={() => copy(value, `${label} copied`)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography color="text.secondary">No configuration is available for this reader.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          disabled={!config}
          onClick={() => config && copy(JSON.stringify(config, null, 2), 'MQTT configuration copied')}
        >
          Copy All
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReaderMqttConfigDialog;
